import type { SetStoreFunction } from "solid-js/store";

import type { State } from "..";

import { TypeAuth } from "./Auth";
import { TypeDraft } from "./Draft";
import { TypeExperiments } from "./Experiments";
import { TypeKeybinds } from "./Keybinds";
import { TypeLayout } from "./Layout";
import { TypeLinkSafety } from "./LinkSafety";
import { TypeLocale } from "./Locale";
import { TypeNotificationOptions } from "./NotificationOptions";
import { TypeOrdering } from "./Ordering";
import { TypeReleaseNotes } from "./ReleaseNotes";
import { TypeSettings } from "./Settings";
import { TypeSounds } from "./Sounds";
import { TypeSynchronisation } from "./Sync";
import { TypeTheme } from "./Theme";
import { TypeVoice } from "./Voice";

export type Store = {
  auth: TypeAuth;
  draft: TypeDraft;
  experiments: TypeExperiments;
  keybinds: TypeKeybinds;
  layout: TypeLayout;
  linkSafety: TypeLinkSafety;
  locale: TypeLocale;
  notifications: TypeNotificationOptions;
  ordering: TypeOrdering;
  "release-notes": TypeReleaseNotes;
  settings: TypeSettings;
  sounds: TypeSounds;
  sync: TypeSynchronisation;
  theme: TypeTheme;
  voice: TypeVoice;
};

/**
 * Abstract store implementation
 */
export abstract class AbstractStore<T extends keyof Store, D> {
  /**
   * Marker used to determine whether this is a store
   */
  private readonly _storeHint = true;

  /**
   * Reference to the current state
   */
  protected readonly state: State;

  /**
   * This store's key
   */
  private readonly key: T;

  /**
   * Construct a new store
   * @param state Reference to current state
   * @param key This store's key
   */
  constructor(state: State, key: T) {
    this.state = state;
    this.key = key;
  }

  /**
   * Get this store's key
   * @returns Key
   */
  getKey(): T {
    return this.key;
  }

  /**
   * Set some value in this store
   */
  protected set: SetStoreFunction<Store[T]> = (...args: unknown[]) => {
    (this.state.set as unknown as (...args: unknown[]) => void)(
      this.key,
      ...args,
    );
  };

  /**
   * Get this store's value
   */
  protected get() {
    return this.state.get(this.key);
  }

  /**
   * Hydrate external context
   */
  abstract hydrate(): void;

  /**
   * Generate default values
   */
  abstract default(): D;

  /**
   * Validate the given data to see if it is compliant and return a compliant object
   */
  abstract clean(input: Partial<D>): D;

  /**
   * Narrow the in-memory value down to what belongs on disk.
   *
   * The store is seeded from `default()` and the disk write serialises whatever
   * sits in memory, so without this every default is written into the profile
   * the first time anything is saved. Once there, it is indistinguishable from
   * a deliberate choice, and no later change to that default can reach the
   * user - the frozen copy answers first. Overriding this lets a store keep the
   * defaults out of the file so they stay resolved live.
   *
   * Identity by default: a store that has no notion of an unset key is stored
   * exactly as it is held.
   */
  toDisk(data: D): Partial<D> {
    return data;
  }

  /**
   * Drop everything still sitting at its default.
   *
   * Ready-made body for `toDisk` in a store whose defaults are plain values.
   * Comparison is by identity, so an object is never treated as a default and
   * is kept as it is - a store with nested defaults has to say what it means
   * itself rather than get it silently wrong here.
   *
   * A value deliberately set to the current default is dropped too. On disk
   * the two are one value and nothing distinguishes them, so the choice
   * follows the default if it later moves.
   */
  protected dropDefaults(data: D): Partial<D> {
    const defaults = this.default() as Record<string, unknown>;
    const stored: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(
      data as Record<string, unknown>,
    )) {
      if (value === undefined) continue;
      if (value === defaults[key]) continue;
      stored[key] = value;
    }

    return stored as Partial<D>;
  }
}
